import {
	TUMBLE_HEIGHT,
	TUMBLE_WIDTH,
	type TumbleInput,
	type TumbleState,
	tumbleCells,
	tumbleLanding,
} from "@playpal/core";
import { useEffect, useMemo, useRef } from "react";
import { useGestures } from "../lib/useGestures";

/**
 * The seven shapes, one colour each, so a child can tell at a glance which
 * block is which — and, more usefully, which part of the stack came from where.
 */
const PIECE_COLOURS = [
	"bg-cyan-400",
	"bg-blue-500",
	"bg-orange-400",
	"bg-yellow-400",
	"bg-green-500",
	"bg-fuchsia-500",
	"bg-rose-500",
] as const;

/** The world's width over its height — what the stage scales to fit. */
export const TUMBLE_ASPECT = TUMBLE_WIDTH / TUMBLE_HEIGHT;

/**
 * The board itself, sized by whatever it is put inside.
 *
 * Hidden from a screen reader, like the snake and for the same reason: two
 * hundred cells changing twelve times a second describe nothing anybody could
 * act on. The score above the board is a live region, and that is the part
 * worth saying.
 */
interface FieldProps {
	state: TumbleState;
	onPress: (input: TumbleInput) => void;
	live: boolean;
}

export function TumbleField({ state, onPress, live }: FieldProps) {
	const board = useRef<HTMLDivElement>(null);

	/*
	 * The way falling blocks have been played on a phone for as long as phones
	 * have had them: drag the piece where you want it, tap to turn it, flick it
	 * down to drop.
	 *
	 * Dragging rather than a sideways flick because placing a piece means moving
	 * it an exact number of columns, and a flick can only say "that way". One
	 * step is one column of the board as it is actually drawn, measured when the
	 * finger lands, so the piece keeps up with the thumb on any size of screen.
	 *
	 * The whole distance goes in one press. Sending a press per column instead
	 * made a drag across the board into ten of them, and since one press is
	 * applied per simulated step the piece then trailed the thumb by most of a
	 * second.
	 */
	useGestures(board, {
		enabled: live,
		onTap: () => onPress({ move: "rotate" }),
		onDrag: (columns) => onPress({ move: columns > 0 ? "right" : "left", by: Math.abs(columns) }),
		dragStep: () => {
			/*
			 * The pitch from one column to the next, measured off two cells.
			 *
			 * Dividing the board's width by ten looks equivalent and is not: the
			 * board has padding and a gap between every cell, so each column comes
			 * out a few per cent too wide, and a finger dragged the width of the
			 * board arrives one column short of where it is pointing. Which is
			 * precisely the imprecision that makes touch controls feel broken.
			 */
			const cells = board.current?.children;
			if (!cells || cells.length < 2) return 0;
			return cells[1].getBoundingClientRect().left - cells[0].getBoundingClientRect().left;
		},
		onSwipe: (direction) => {
			if (direction === "down") onPress({ move: "drop" });
		},
	});

	/*
	 * One array of two hundred, rebuilt only when something moved.
	 *
	 * The alternative — a settled layer plus the piece drawn over it — needs two
	 * stacking contexts and gets the ghost wrong wherever they overlap. Two
	 * hundred divs at twelve frames a second is nothing next to that.
	 */
	const cells = useMemo(() => {
		const drawn: (number | "ghost")[] = state.cells.slice();

		if (!state.over) {
			const landing = tumbleLanding(state.cells, state.piece, state.rotation, state.x, state.y);
			// Where it will end up if nothing else is pressed. Without this a child
			// is guessing which column they are over, and on a phone they guess wrong.
			for (const at of tumbleCells(state.piece, state.rotation, state.x, landing)) {
				if (at >= 0 && at < drawn.length && drawn[at] === 0) drawn[at] = "ghost";
			}
			for (const at of tumbleCells(state.piece, state.rotation, state.x, state.y)) {
				if (at >= 0 && at < drawn.length) drawn[at] = state.piece + 1;
			}
		}
		return drawn;
	}, [state.cells, state.piece, state.rotation, state.x, state.y, state.over]);

	return (
		<div
			ref={board}
			aria-hidden="true"
			className="grid touch-none select-none gap-px rounded-3xl bg-slate-900/90 p-2 shadow-inner ring-4 ring-slate-800/50"
			style={{
				gridTemplateColumns: `repeat(${TUMBLE_WIDTH}, minmax(0, 1fr))`,
				aspectRatio: `${TUMBLE_WIDTH} / ${TUMBLE_HEIGHT}`,
			}}
		>
			{cells.map((cell, at) => (
				<div
					// The grid is fixed, so its index really is each cell's identity.
					key={at}
					className={`rounded-[15%] ${
						cell === "ghost"
							? "bg-white/15"
							: cell === 0
								? "bg-slate-800/60"
								: PIECE_COLOURS[cell - 1]
					}`}
				/>
			))}
		</div>
	);
}

/**
 * Turn and drop on top, where a thumb rests; steering underneath in the order
 * it appears on screen. Drop is the one that ends a piece, so it sits away from
 * the others rather than beside the one next to it.
 */
export function TumbleControls({
	onPress,
	live,
}: {
	onPress: (input: TumbleInput) => void;
	/** False before the run starts and after it ends — keys stop working then. */
	live: boolean;
}) {
	useKeys(onPress, live);

	return (
		<div className="py-1">
			<div className="mx-auto grid max-w-md grid-cols-3 justify-items-center gap-2">
				<Key label="Turn the block" glyph="🔄" onPress={() => onPress({ move: "rotate" })} />
				<span />
				<Key label="Drop it now" glyph="⏬" onPress={() => onPress({ move: "drop" })} />

				<Key label="Move left" glyph="⬅️" onPress={() => onPress({ move: "left" })} />
				<Key label="Move down" glyph="⬇️" onPress={() => onPress({ move: "down" })} />
				<Key label="Move right" glyph="➡️" onPress={() => onPress({ move: "right" })} />
			</div>
			<p className="mt-2 text-center text-sm font-semibold opacity-60">
				Or drag the block, tap to turn it, and flick down to drop
			</p>
		</div>
	);
}

function Key({ label, glyph, onPress }: { label: string; glyph: string; onPress: () => void }) {
	return (
		<button
			type="button"
			aria-label={label}
			// Pointer-down rather than click, as on the snake: waiting for a click
			// to be confirmed costs most of a step, and reads as being ignored.
			onPointerDown={(event) => {
				event.preventDefault();
				onPress();
			}}
			className="flex h-14 w-full max-w-28 touch-none select-none items-center justify-center rounded-2xl bg-white/15 text-3xl shadow-lg transition active:scale-95"
		>
			<span aria-hidden="true">{glyph}</span>
		</button>
	);
}

const KEY_MOVES: Record<string, TumbleInput["move"] | undefined> = {
	ArrowLeft: "left",
	ArrowRight: "right",
	ArrowDown: "down",
	ArrowUp: "rotate",
	" ": "drop",
};

/** Arrow keys and the space bar, for whoever is playing on a laptop. */
function useKeys(onPress: (input: TumbleInput) => void, live: boolean) {
	useEffect(() => {
		if (!live) return;

		const onKeyDown = (event: KeyboardEvent) => {
			const move = KEY_MOVES[event.key];
			if (!move) return;
			// Space scrolls the page and the arrows do too. Neither is wanted here.
			event.preventDefault();
			onPress({ move });
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onPress, live]);
}
